import { eventReducer as _eventReducer, } from "@withorbit/core";
import { AjvEventsValidator } from "./validation/AjvEventsValidator.js";
const _ajvEventValidator = new AjvEventsValidator();
// The Database offers interfaces for storing and querying events and the entities generated from those events.
export class Database {
    // DatabaseBackend does most of the work here; Database wraps that interface with the entity-generation logic.
    _backend;
    // The event reducer specifies how entities are generated from events. Note that Database doesn't know anything about any specific event or entity types; it's a general event source implementation.
    _eventReducer;
    // Validator to ensure that the events match type declarations of the events
    _eventValidator;
    constructor(backend, eventReducer = _eventReducer, eventValidator = _ajvEventValidator) {
        this._backend = backend;
        this._eventReducer = eventReducer;
        this._eventValidator = eventValidator;
    }
    close() {
        return this._backend.close();
    }
    async putEvents(events) {
        const eventValidationResult = this._eventValidator.validateEvents(events);
        if (eventValidationResult !== true) {
            return new Promise((_, reject) => reject(eventValidationResult));
        }
        if (events.length === 0)
            return [];
        let output;
        await this._backend.updateEntities(events, async (eventsPendingSave, currentEntityRecordMap) => {
            const eventsByEntityID = new Map();
            for (const event of eventsPendingSave) {
                eventsByEntityID.set(event.entityID, [
                    ...(eventsByEntityID.get(event.entityID) ?? []),
                    event,
                ]);
            }
            const entityUpdates = [];
            for (const [id, events] of eventsByEntityID.entries()) {
                try {
                    const updates = await this._computeUpdatedEntitySnapshot(id, currentEntityRecordMap.get(id) ?? null, events);
                    entityUpdates.push(updates);
                }
                catch {
                    // Orphan events (e.g. repetition without ingest) can arrive
                    // during sync when events are pulled out of causal order.
                    // Skip this entity — its events are still stored, so the next
                    // sync that brings the missing ingest will reconstruct it.
                }
            }
            output = entityUpdates.flat();
            return new Map(entityUpdates.map((updates) => {
                const lastUpdate = updates[updates.length - 1];
                return [
                    lastUpdate.entity.id,
                    {
                        entity: lastUpdate.entity,
                        lastEventID: lastUpdate.event.id,
                        lastEventTimestampMillis: lastUpdate.event.timestampMillis,
                    },
                ];
            }));
        });
        if (output === undefined) {
            throw new Error(`Inconsistent: output didn't get set in updateEntities`);
        }
        // Only return entries corresponding to the new events.
        const eventIDs = new Set(events.map(({ id }) => id));
        return output.filter(({ event: { id } }) => eventIDs.has(id));
    }
    getEvents(eventIDs) {
        return this._backend.getEvents(eventIDs);
    }
    async getEntities(entityIDs) {
        const records = await this._backend.getEntities(entityIDs);
        return new Map([...records.entries()].map(([id, record]) => [id, record.entity]));
    }
    // Returns events in an arbitrary order which is stable on this client (i.e. so paging using afterID is safe), but which is not guaranteed to be consistent across clients.
    listEvents(query) {
        return this._backend.listEvents(query);
    }
    // Returns entities in an arbitrary order which is stable on this client (i.e. so paging using afterID is safe), but which is not guaranteed to be consistent across clients.
    async listEntities(query) {
        const records = await this._backend.listEntities(query);
        return records.map(({ entity }) => entity);
    }
    // Reads a small, top-level value, useful for configuration and lightweight state tracking.
    async getMetadataValues(keys) {
        return await this._backend.getMetadataValues(keys);
    }
    // Writes a small, top-level value, useful for configuration and lightweight state tracking.
    async setMetadataValues(values) {
        await this._backend.setMetadataValues(values);
    }
    async _computeUpdatedEntitySnapshot(entityID, currentRecord, newEvents) {
        const _computeUpdatedEntityWithEvents = (snapshot, events) => {
            const output = [];
            let currentEntity = snapshot;
            for (const event of events) {
                try {
                    const entity = this._eventReducer(currentEntity, event);
                    output.push({ event, entity });
                    currentEntity = entity;
                }
                catch (error) {
                    throw new Error(`Error updating entity ${entityID}: ${error}; snapshot: ${JSON.stringify(snapshot, null, "\t")}; new events: ${JSON.stringify(events, null, "\t")}`);
                }
            }
            return output;
        };
        newEvents.sort(compareEvents);
        // Most of the time, new events will be ordered after old events (in terms of local client time), which means we can apply them directly on top of our existing snapshot.
        if (newEvents.length > 0 &&
            (!currentRecord ||
                newEvents[0].timestampMillis > currentRecord.lastEventTimestampMillis)) {
            // Apply the new events on top of the pre-existing snapshot.
            return _computeUpdatedEntityWithEvents(currentRecord?.entity ?? null, newEvents);
        }
        else {
            // We've got an out-of-order event (or no prior snapshot). We'll recompute the snapshot from scratch.
            const events = (await this._backend.listEvents({
                predicate: ["entityID", "=", entityID],
            })).concat(newEvents);
            if (events.length === 0) {
                throw new Error(`Attempting to update snapshot for entity ${entityID}, but no events are present.`);
            }
            events.sort(compareEvents);
            return _computeUpdatedEntityWithEvents(null, events);
        }
    }
}
// Stable sorting functions for events
function compareEvents(a, b) {
    if (a.timestampMillis === b.timestampMillis) {
        return 0; // Preserve original event sequence when timestamp match.
    }
    else {
        return a.timestampMillis - b.timestampMillis;
    }
}
//# sourceMappingURL=database.js.map